import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import { Add, Delete, Files, Replace } from '@factorialco/factorial-one-react/icons/app'
import {
  BulkUpdateTasksDocument,
  ProjectTasksDataCollectionDocument,
  ProjectTasksDataCollectionItemFragment,
} from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { isValidTaskStatus } from 'modules/tasks/lib/status'
import { TaskIdentifier } from 'modules/tasks/lib/task'

import { getStatusColor, getStatusLabel } from '../../labelTags/TaskStatusTag'
import { DeleteModal } from './modals/DeleteModal'
import { DuplicateModal } from './modals/DuplicateModal'

const MODAL: {
  [key in Exclude<BulkActionType, 'ChangeStatus'>]: React.ComponentType<{
    onCloseModal: () => void
    ids: number[]
    projectName: string
  }>
} = {
  ['Duplicate']: DuplicateModal,
  ['Delete']: DeleteModal,
}

export type BulkActionType = 'ChangeStatus' | 'Delete' | 'Duplicate'

interface ProjectTasksDataCollectionProps {
  projectId: number
  projectName: string
  onClickCreateTask: () => void
}

const ProjectTasksDataCollection = ({
  projectId,
  projectName,
  onClickCreateTask,
}: ProjectTasksDataCollectionProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [actionType, setActionType] = useState<Exclude<BulkActionType, 'ChangeStatus'> | null>(null)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const ActionModal = useMemo(() => (actionType ? MODAL[actionType] : null), [actionType])
  const onCloseModal = () => setActionType(null)

  const [bulkUpdateTasks, { loading: bulkUpdateTasksLoading }] = useMutation(
    BulkUpdateTasksDocument,
    {
      extractorFn: (data) => data.tasksMutations.bulkUpdateTasksTask,
    }
  )

  // TODO: get current view from somewhere, this is not provided by datacollection currently
  const fromKanban = false

  const source = useGraphqlDataSource({
    document: ProjectTasksDataCollectionDocument,
    mapData: (data) => data.projectManagement.projectTasksConnection.nodes,
    mapVariables: ({ search, sortings }) => ({
      projectIds: [projectId],
      search,
      sortOrder: sortings?.[0],
    }),
    mapPagination: (data) => ({
      total: data.projectManagement.projectTasksConnection.totalCount ?? 0,
    }),
    itemUrl: (item) =>
      `${navigation.tasks.taskIdAndIdentifier.fullPath({
        taskId: Number(item.task?.id),
        identifier: TaskIdentifier.Project,
      })}?view=${fromKanban ? 'kanban' : 'table'}`,
    primaryActions: () => {
      return {
        label: i18n.t('project_management.tasks.add_button'),
        icon: Add,
        onClick: () => {
          track(trackingEvents.projectManagement.tasks.createProjectTaskButton, {
            origin: fromKanban ? 'kanban' : 'datatable',
          })

          onClickCreateTask()
        },
      }
    },
    selectable: (item) => item.task?.id,
    bulkActions: ({ selectedCount }) => {
      return {
        primary: [
          {
            label: i18n.t('tasks.status.change_status'),
            id: 'change-status',
            icon: Replace,
            loading: bulkUpdateTasksLoading,
          },
          {
            label: i18n.t('tasks.status.todo'),
            id: 'change-status;todo',
          },
          {
            label: i18n.t('tasks.status.done'),
            id: 'change-status;done',
          },
          {
            label: i18n.t('tasks.status.in_progress'),
            id: 'change-status;in_progress',
          },
          {
            label: i18n.t('tasks.status.discarded'),
            id: 'change-status;discarded',
          },
        ],
        secondary: [
          {
            label: i18n.t('common.delete'),
            icon: Delete,
            critical: true,
            id: 'delete',
          },
          {
            label: i18n.t('common.duplicate'),
            icon: Files,
            id: 'duplicate',
          },
          ...(selectedCount === 1
            ? [
                {
                  label: i18n.t('common.edit'),
                  id: 'edit',
                },
              ]
            : []),
        ],
      }
    },
    search: {
      enabled: true,
    },
  })

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={async (action, selectedItems) => {
          if (action.startsWith('change-status')) {
            const [, status] = action.split(';')

            if (status && isValidTaskStatus(status)) {
              const result = await bulkUpdateTasks({
                variables: {
                  tasks: selectedItems.itemsStatus.map((item) => ({
                    id: item.item.task?.id ?? 0,
                    status,
                    identifier: TaskIdentifier.Project,
                  })),
                },
                refetchQueries: [
                  {
                    query: ProjectTasksDataCollectionDocument,
                    variables: { projectIds: [projectId] },
                  },
                ],
              })

              const handled = handleResult(result, (_) => {})
              if (handled.success) {
                app.setFlash({
                  type: 'correct',
                  message: i18n.t('common.saved_successfully'),
                })
              }
            }
          }

          switch (action) {
            case 'delete':
              setActionType('Delete')
              setSelectedIds(selectedItems.itemsStatus.map((item) => item.item.id))
              break
            case 'duplicate':
              setActionType('Duplicate')
              setSelectedIds(selectedItems.itemsStatus.map((item) => item.item.id))
              break
            case 'edit':
              const [taskId] = selectedItems.itemsStatus.map((item) => item.item.id)

              navigate(
                navigation.projects.id.details.tasks.taskId.fullPath({
                  id: projectId,
                  taskId: Number(taskId),
                })
              )
              break
          }
        }}
        visualizations={[
          // {
          //   type: 'kanban',
          //   options: {}
          // },
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('project_management.table.tasks.headers.name'),
                  render: (projectTask: ProjectTasksDataCollectionItemFragment) => ({
                    type: 'text',
                    value: projectTask.task?.name,
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.subproject'),
                  render: (projectTask: ProjectTasksDataCollectionItemFragment) => ({
                    type: 'text',
                    value: projectTask.subproject?.name,
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.assignees'),
                  render: (projectTask: ProjectTasksDataCollectionItemFragment) => ({
                    type: 'avatarList',
                    value: {
                      avatarList:
                        projectTask.task?.assigneesConnection.nodes.map((node) => ({
                          type: 'person',
                          firstName: node.firstName,
                          lastName: node.lastName,
                          src: node.avatar ? buildUrl(node.avatar) : undefined,
                        })) ?? [],
                      max: 2,
                    },
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.status'),
                  render: (projectTask: ProjectTasksDataCollectionItemFragment) => ({
                    type: 'status',
                    value: {
                      label: projectTask.task?.status
                        ? getStatusLabel(projectTask.task?.status)
                        : '-',
                      status: projectTask.task?.status
                        ? getStatusColor(projectTask.task?.status)
                        : 'neutral',
                    },
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.starts_on'),
                  render: (projectTask: ProjectTasksDataCollectionItemFragment) => ({
                    type: 'date',
                    value: projectTask.task?.startsOn
                      ? new Date(projectTask.task?.startsOn)
                      : undefined,
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.due_on'),
                  render: (projectTask: ProjectTasksDataCollectionItemFragment) => ({
                    type: 'date',
                    value: projectTask.task?.dueOn ? new Date(projectTask.task?.dueOn) : undefined,
                  }),
                },
                {
                  label: i18n.t('project_management.table.tasks.headers.completed_at'),
                  render: (projectTask: ProjectTasksDataCollectionItemFragment) => ({
                    type: 'date',
                    value: projectTask.task?.completedAt
                      ? new Date(projectTask.task?.completedAt)
                      : undefined,
                  }),
                },
              ],
            },
          },
        ]}
      />
      {ActionModal && (
        <ActionModal onCloseModal={onCloseModal} ids={selectedIds} projectName={projectName} />
      )}
    </>
  )
}

export default ProjectTasksDataCollection
